import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Address, AddressDetails, AddressPlaceType, Country, ShippingEstimate } from '@pedix-workspace/utils';
import { TranslocoDirective, TranslocoService } from '@ngneat/transloco';
import { IconEditComponent } from '@pedix-workspace/angular-ui-icons';
import {
  InputTextComponent,
  InputRadioGroupComponent,
} from '@pedix-workspace/angular-ui-form-reactive';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import { MapAddressComponent } from '@pedix-workspace/angular-ui-map';
import {
  AnalyticsService,
  GeoIpService,
  GeoIpState,
} from '@pedix-workspace/pedixapp-core-services';
import { ShippingCostResponse } from '@pedix-workspace/shared-delivery';
import { ShippingCostResponseComponent } from '../shipping-cost-response/shipping-cost-response.component';
import { ModalDialogComponent } from '@pedix-workspace/angular-ui-modal';
import { AlertComponent } from '@pedix-workspace/angular-ui-alert';

export type PlaceTypeOption = { label: string; type: AddressPlaceType };

@Component({
  selector: 'pxw-address',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.scss'],
  providers: [],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    InputTextComponent,
    InputRadioGroupComponent,
    FormsModule,
    ButtonComponent,
    IconEditComponent,
    MapAddressComponent,
    ShippingCostResponseComponent,
    ModalDialogComponent,
    AlertComponent,
  ],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class AddressComponent implements OnInit {
  private readonly t = inject(TranslocoService);
  private readonly analytics = inject(AnalyticsService);
  private readonly geoIpService = inject(GeoIpService);

  readonly address = input.required<Address | null>();
  readonly addressChange = output<Address>();

  readonly workerServicesUrl = input.required<string>();
  readonly disableAddressInMap = input.required<boolean | null>();
  readonly isFormSubmitted = input.required<boolean>();
  readonly shippingCostResponse = input.required<ShippingCostResponse | null>();
  readonly calculatingShippingCost = input.required<boolean>();
  readonly countryCodes = input<Country[] | null>();
  readonly selectedParcelOption = input<ShippingEstimate | null>(null);

  readonly selectedParcelOptionChange = output<ShippingEstimate | null>();

  readonly geoIpState = signal<GeoIpState | null>(null);
  readonly street = signal<string | null>(null);
  readonly addressDetails = signal<AddressDetails | null>(null);
  readonly placeType = signal<AddressPlaceType | null>(null);
  readonly placeName = signal<string | null>(null);
  readonly lot = signal<string | null>(null);
  readonly floorAndNumber = signal<string | null>(null);
  readonly references = signal<string | null>(null);

  // Modal para código postal
  readonly showPostalCodeModal = signal<boolean>(false);
  readonly manualPostalCode = signal<string | null>(null);
  readonly postalCodeModal = viewChild<ModalDialogComponent>('postalCodeModal');
  private readonly isProcessingPostalCode = signal<boolean>(false);

  readonly placeTypes = computed<PlaceTypeOption[]>(() => [
    { label: this.t.translate('address.placeTypeHouse'), type: 'house' },
    { label: this.t.translate('address.placeTypeHousingState'), type: 'housing-state' },
    { label: this.t.translate('address.placeTypeBuilding'), type: 'building' },
    { label: this.t.translate('address.placeTypeBusiness'), type: 'business' },
    { label: this.t.translate('address.placeTypeOther'), type: 'other' },
  ]);
  readonly mapInstructions = computed<string[]>(() => [
    this.t.translate('address.mapInstructions1'),
    this.t.translate('address.mapInstructions2'),
    this.t.translate('address.mapInstructions3'),
  ]);
  readonly referencesRequired = computed<boolean>(() => {
    if (this.disableAddressInMap()) {
      return false;
    }
    if (this.placeType() === 'other') {
      return true;
    }
    if (this.placeType() === 'house' && !this.addressDetails()?.streetNumber) {
      return true;
    }
    return false;
  });

  // Detectar si se necesita código postal para paquetería
  readonly needsPostalCodeForParcel = computed<boolean>(() => {
    const response = this.shippingCostResponse();
    return !!(response?.postalCodeRequired);
  });

  readonly canConfirmPostalCode = computed<boolean>(() => {
    const postalCode = this.manualPostalCode();
    return !!(postalCode && postalCode.trim().length >= 4);
  });



  constructor() {
    effect(() => {
      this.addressChange.emit({
        street: this.street(),
        addressDetails: this.addressDetails(),
        placeType: this.placeType(),
        placeName: ['housing-state', 'business', 'building', 'other'].includes(this.placeType()!)
          ? this.placeName()
          : null,
        lot: this.placeType() === 'housing-state' ? this.lot() : null,
        floorAndNumber: this.placeType() === 'building' ? this.floorAndNumber() : null,
        references: this.references(),
      });
    });

    // Mostrar modal automáticamente cuando se necesite código postal
    effect(() => {
      const needsCode = this.needsPostalCodeForParcel();
      const currentAddressDetails = this.addressDetails();
      const modal = this.postalCodeModal();
      const isProcessing = this.isProcessingPostalCode();
      
      // Solo abrir el modal si:
      // 1. Se necesita código postal
      // 2. Hay detalles de dirección
      // 3. No hay código postal en la dirección
      // 4. No se está procesando una confirmación
      // 5. El modal no está ya abierto
      if (needsCode && 
          currentAddressDetails && 
          !currentAddressDetails.postalCode && 
          !isProcessing &&
          modal && 
          !this.showPostalCodeModal()) {
        this.showPostalCodeModal.set(true);
        modal.open(this.onConfirmPostalCode);
      } else if (currentAddressDetails?.postalCode && modal && this.showPostalCodeModal()) {
        this.showPostalCodeModal.set(false);
        modal.close();
      }
    }, { allowSignalWrites: true });
  }

  ngOnInit() {
    this.street.set(this.address()?.street || null);
    this.addressDetails.set(this.address()?.addressDetails || null);
    this.placeType.set(this.address()?.placeType || null);
    this.placeName.set(this.address()?.placeName || null);
    this.lot.set(this.address()?.lot || null);
    this.floorAndNumber.set(this.address()?.floorAndNumber || null);
    this.references.set(this.address()?.references || null);

    if (!this.disableAddressInMap()) {
      this.geoIpService.retrieveGeoIpState(this.workerServicesUrl()).then(geoIpState => {
        this.geoIpState.set(geoIpState);
      });
    }
  }

  onUpdateAddressDetails(addressDetails: AddressDetails | null) {
    if (addressDetails) {
      this.analytics.fetchAddressDetails('end-order');
      
      // Si la nueva dirección ya tiene código postal, limpiar el manual
      if (addressDetails.postalCode) {
        this.manualPostalCode.set(null);
        this.showPostalCodeModal.set(false);
      }
    }
    
    this.addressDetails.set(addressDetails);
  }

  onParcelOptionSelected(option: ShippingEstimate | null) {
    this.selectedParcelOptionChange.emit(option);
  }

  onOpenPostalCodeModal() {
    this.showPostalCodeModal.set(true);
  }

  onModalOpenChange(isOpen: boolean) {
    this.showPostalCodeModal.set(isOpen);
    
    // Si el modal se cerró manualmente (no por confirmación) 
    // y aún necesitamos código postal, programar reapertura
    if (!isOpen && 
        this.needsPostalCodeForParcel() && 
        !this.isProcessingPostalCode() &&
        !this.addressDetails()?.postalCode) {
      
      // Dar un pequeño tiempo para que el usuario pueda ver que se cerró
      // antes de volver a abrir automáticamente
      setTimeout(() => {
        const modal = this.postalCodeModal();
        const stillNeedsCode = this.needsPostalCodeForParcel();
        const hasPostalCode = this.addressDetails()?.postalCode;
        
        if (modal && stillNeedsCode && !hasPostalCode && !this.showPostalCodeModal()) {
          this.showPostalCodeModal.set(true);
          modal.open(this.onConfirmPostalCode);
        }
      }, 1000); // Esperar 1 segundo antes de volver a abrir
    }
  }

  onConfirmPostalCode = async () => {
    this.isProcessingPostalCode.set(true);
    
    try {
      const currentAddressDetails = this.addressDetails();
      const postalCode = this.manualPostalCode()?.trim();

      if (!currentAddressDetails || !postalCode) {
        throw new Error('Datos incompletos');
      }

      // Actualizar addressDetails con el código postal
      const updatedAddressDetails: AddressDetails = {
        ...currentAddressDetails,
        postalCode: postalCode,
      };

      this.addressDetails.set(updatedAddressDetails);
      this.showPostalCodeModal.set(false);
      this.manualPostalCode.set(null);
      
      return true;
    } finally {
      // Resetear el flag de procesamiento después de un breve delay
      setTimeout(() => {
        this.isProcessingPostalCode.set(false);
      }, 500);
    }
  };
}
